import { RARITIES } from '../cars/carSchema';
import { specValue } from '../cars/carFigures';
import { duelSpec } from './duelSpecs';

/**
 * What a card is worth, and whether one beats another (SPEC-011 PR-11.1).
 *
 * The smallest of the four rules modules and the one the other three are built
 * on. It knows two things: how expensive a card is to lose, and which of two
 * cards wins a named figure in a named direction. It knows nothing about hands,
 * pots or turns.
 *
 * Pure, and free of every module in the tree except the two tables it reads —
 * the rarity vocabulary and the figure addressing.
 */

/**
 * The two directions a round can be played in.
 *
 * The same words `duelSpecs` uses, because they are the same idea and one of
 * them arrives from there. A round starts at the figure's own direction and a
 * counter-call inverts it (SPEC-011 D-7).
 */
export const DIRECTIONS = ['higher', 'lower'];

/**
 * What a card costs its owner, by rarity.
 *
 * Rarity rather than a figure, and that is the whole economy of the game
 * (SPEC-011 D-6). Valuing a pot on a figure would mean choosing WHICH figure,
 * which is the argument every round is already about; valuing it on rarity
 * means the F40 is expensive in the rounds it is bad at as well as the ones it
 * wins. That asymmetry is what makes the cheapest card that wins the correct
 * overtake, and what makes playing a good one frightening.
 *
 * Keyed off `carSchema.RARITIES`, so the ribbons the brand sheet draws and the
 * prices here cannot drift apart. A rarity added to the vocabulary without a
 * price here would be worth nothing at all, which is why
 * `overtakeCards.test.js` asserts the two key sets match rather than asserting
 * a fixed count — and why adding `unicorn` to the schema failed that test
 * before it reached this table (SPEC-016 D-5).
 *
 * FOUR IS THE CEILING AND FIVE WAS REFUSED. The spec that added the fourth tier
 * opened with two of them, priced 4 and 5; a hand is five cards (`HAND_SIZE`),
 * so a card worth five is a whole hand's pot in one play and a swing this game
 * was never balanced for. Four extends the existing one-step spacing and keeps
 * D-6's asymmetry intact: the expensive card is expensive in the rounds it
 * loses as well as the ones it wins.
 */
export const CARD_VALUES = {
    common: 1,
    rare: 2,
    legendary: 3,
    unicorn: 4,
};

/**
 * What this card is worth in a pot.
 *
 * An unknown or absent rarity is worth the cheapest thing on the table rather
 * than nothing. Zero would make a malformed record free to throw into every
 * round it could not win, which is a strictly better card than a real common —
 * and the catalogue is fetched, so a record that arrives short is a state this
 * reaches in the wild rather than an impossible one.
 */
export function cardValue(car) {
    return CARD_VALUES[car?.rarity] ?? CARD_VALUES.common;
}

/** The direction this figure is played in before anybody counter-calls it. */
export function baseDirection(spec) {
    return duelSpec(spec)?.wins ?? 'higher';
}

/** The other one. A counter-call is exactly this applied once (SPEC-011 D-7). */
export function invertDirection(direction) {
    return direction === 'lower' ? 'higher' : 'lower';
}

/**
 * Can this card be played on this figure at all?
 *
 * The RX-7 has no cylinder count because it is a Wankel, and six of the fourteen
 * carry no production figure. `specValue` already answers null for every one of
 * them, and null here means the card is not a legal play rather than a play that
 * loses (SPEC-011 D-4). A zero would be a card that beats nothing and loses to
 * everything, which is a different and much worse rule: it would make the RX-7
 * a free discard on cylinders instead of a card that simply cannot go there.
 */
export function isPlayableOn(car, spec) {
    return specValue(car, spec) !== null;
}

/**
 * Does `challenger` beat `standing` on this figure, played in this direction?
 *
 * STRICTLY, and with no minimum margin — which is the one place this module
 * deliberately parts company with `duelSpecs.duelWinner`. That function refuses
 * a pairing inside `minimumMargin` because a duel asking which of two cars one
 * km/h apart is faster is a coin toss wearing a question, and a reader who
 * cannot know the answer should not be asked it.
 *
 * None of that transfers here (SPEC-011 D-2). The reader is holding both cards
 * and can read both numbers; choosing to spend a card for one km/h is a
 * deliberate, visible, slightly desperate overtake, and it is a good moment in a
 * card game rather than an unfair question.
 *
 * A TIE DOES NOT BEAT, and that falls out of the comparison rather than being
 * written anywhere (SPEC-011 D-3). It is still the most visible rule at the
 * table: four cars in this catalogue are V12s, so the first V12 played on
 * cylinders wins the round outright and the other three cannot answer it.
 */
export function beatsOn(challenger, standing, spec, direction) {
    const attacking = specValue(challenger, spec);
    const defending = specValue(standing, spec);

    if (attacking === null || defending === null) {
        return false;
    }

    return direction === 'lower' ? attacking < defending : attacking > defending;
}

/**
 * The rarity vocabulary this module prices, for the test that holds the two
 * tables together.
 *
 * Exported rather than re-imported at the call site so there is one spelling of
 * "the rarities that have a price", and so the test reads as a comparison of two
 * lists rather than as a hand-written expectation of three.
 */
export const PRICED_RARITIES = RARITIES;
